/* eslint-disable max-len */
import * as React from 'react';
import { HocOptions } from '../common/generic-hoc.types';
import { StylesProps } from '../../theme/jss-types';
import { consignmentDetailsIntStyles } from './consignment-details.styles';
import GenericHoc from '../common/generic-hoc';
import { snakeToPretty } from 'library/lodash-helper';
import {
    Button,
    Image,
    Tooltip,
    Collapse,
} from 'antd';
import { DetailsToShowKeysDomestic, DetailsToShowKeysIntl } from './consignment-details.constants';
import { useTranslation } from 'react-i18next';
import DocumentDetailsModal from './document-details-modal';
import {
    InfoCircleOutlined, ClockCircleTwoTone, EnvironmentFilled,
} from '@ant-design/icons';
import TableModal from '../table-modal/tableModal';
import { RouterProps } from 'library/Types';
import { PAGE_NAMES } from 'types/page.types';
import { ReduxStore } from 'reducers/redux.types';
import { ViewData } from 'types/view.types';
import { isEmpty } from 'lodash';
import { prettyStatusNames } from './consignments.constants';
import ContentFilledIcon from 'assets/contents-filled';
import useIsMobile from 'hooks/useIsMobile';
import { formatDateTime } from './consignments.helper';

interface ConsignmentDetailedInternationalProps extends StylesProps<ReturnType<typeof consignmentDetailsIntStyles>>,
    RouterProps {
    consignment: Record<string, any>;
    consignmentSectionList: ViewData[][];
    separateChildCN: boolean;
    selectedTab: string;
    expandPage: boolean;
    currencySymbol: string;
    completeCustomFieldsList: any;
}

const ConsignmentDetailedInternational = (props: ConsignmentDetailedInternationalProps) => {
    const {
        classes,
        consignment,
        consignmentSectionList,
        separateChildCN,
        location,
        selectedTab,
        expandPage,
        currencySymbol,
        completeCustomFieldsList,
    } = props;
    const [imageUrl, setImageUrl] = React.useState<string | null>(null);
    const { t, i18n } = useTranslation();
    const [isVisible, setIsVisible] = React.useState<boolean>(false);
    const [modalRow, setModalRow] = React.useState<any>(null);
    const [isPriceDetailsModalVisible, setIsPriceDetailsModalVisible] = React.useState<boolean>(false);
    const [priceDataList, setPriceDataList] = React.useState<any>([]);
    // eslint-disable-next-line no-spaced-func
    const [viewDataKeyList, setViewDataKeyList] = React.useState<Record<string, (string | undefined)[]>>({});
    const [viewDataKeyLabelMap, setViewDataKeyLabelMap] = React.useState<Record<string, string>>({});
    const [currentPieceNumber, setCurrentPieceNumber] = React.useState(0);
    const piecesArray = consignment?.pieces_detail;
    const isMobile = useIsMobile();
    const getDetailsToShowKeyList = () => {
        if (!isEmpty(viewDataKeyList)) return viewDataKeyList;
        return consignment?.is_international ? DetailsToShowKeysIntl : DetailsToShowKeysDomestic;
    };

    React.useEffect(() => {
        const viewId = new URLSearchParams(location.search)?.get('view');

        if (viewId) {
            const detailsToShowKeysList: Record<string, (string | undefined)[]> = getDetailsToShowKeyList();
            const viewData = consignmentSectionList?.flat().find((view) => view.id === viewId);
            const consignmentFieldList: Record<string, string> = viewData?.metadata?.fieldList?.reduce(
                (obj: Record<string, string>, item) => {
                    if (item?.enable) {
                        return { ...obj, [item.id]: item.name };
                    }
                    return obj;
                }, {},
            ) || {};
            const viewDataKeys: Record<string, (string | undefined)[]> = {};

            if (!isEmpty(consignmentFieldList)) {
                Object.keys(detailsToShowKeysList).forEach((key) => {
                    viewDataKeys[key] = detailsToShowKeysList[key]?.filter(
                        (item) => item && consignmentFieldList?.[item],
                    ) || [];
                });
                setViewDataKeyList(viewDataKeys);
                setViewDataKeyLabelMap(consignmentFieldList);
            }
        }
    }, [consignmentSectionList]);

    const labelValuePair = (key: string) => {
        const data = {
            label: snakeToPretty(key),
            value: consignment[key],
        };

        if (viewDataKeyLabelMap[key]) {
            data.label = viewDataKeyLabelMap[key];
        } else {
            switch (key) {
                case 'load_type':
                    data.label = 'Item Type';
                    break;
                case 'num_pieces':
                    data.label = 'No. of Pieces';
                    break;
                case 'meis':
                    data.label = 'MEIS';
                    break;
                case 'is_risk_surcharge_applicable':
                    data.label = 'Risk Surcharge';
                    data.value = data.value !== null && data.value !== undefined ? data.value.toString() : '';
                    break;
                case 'length':
                case 'width':
                case 'height':
                    data.value = data.value ? `${consignment[key]}  cm` : '';
                    break;
                case 'weight':
                    data.value = data.value ? `${consignment[key]}  Kg` : '';
                    break;
                case 'given_length':
                case 'given_width':
                case 'given_height':
                    data.value = data.value ? `${consignment[key]}  cm` : '';
                    break;
                case 'given_weight':
                    data.value = data.value ? `${consignment[key]}  Kg` : '';
                    break;
                case 'fob_value':
                case 'freight_cost':
                    data.value = consignment[key]
                        ? `${consignment[key]}  ${consignment[`${key}_currency`] || ''}`
                        : '';
                    break;
                case 'total_gst_paid_amount':
                    data.value = consignment[key]
                        ? `${consignment[key]}  ${consignment.total_gst_paid_currency || ''}`
                        : '';
                    break;
                case 'consignor_kyc_front_image':
                case 'consignor_kyc_back_image':
                    if (data.value) {
                        data.value = (
                            <Button
                                type="link"
                                onClick={() => {
                                    setImageUrl(consignment[key]);
                                }}
                            >
                                View
                            </Button>
                        );
                    }
                    break;
                case 'sender_name':
                case 'sender_phone':
                case 'sender_address_line_1':
                case 'sender_address_line_2':
                case 'sender_country':
                case 'sender_state':
                case 'sender_city':
                case 'sender_service_time_mins':
                    data.label = data.label.replace('Sender Service Time Mins', 'Sender Service Time (mins)');
                    break;
                case 'destination_service_time_mins':
                    data.label = 'Destination Service Time (mins)';
                    break;
                case 'sender_pincode':
                    data.label = data.label.replace('Sender', 'Origin');
                    break;
                case 'origin_type':
                    data.label = data.label.replace('Origin', 'Sender');
                    break;
                case 'against_bond_lut':
                    data.label = 'Against Bond LUT';
                    if (data.value === 'B') data.value = 'Against Bond';
                    else if (data.value === 'U') data.value = 'Undertaking';
                    break;
                case 'international_detail_shipment_purpose':
                    data.label = 'Shipment Purpose';
                    break;
                case 'delivery_terms':
                    data.label = 'Shipment Terms';
                    break;
                case 'consignor_pan':
                    data.label = 'Consignor Pan Id';
                    break;
                case 'booking_service_type_id':
                    data.label = 'Booking Service Type';
                    break;
                case 'courier_partner_reference_number':
                    data.label = 'Carrier AWB No.';
                    break;
                case 'courier_partner':
                    data.label = 'Carrier';
                    break;
                case 'transport_mode':
                    data.label = 'Transport Mode';
                    break;
                case 'document_list':
                    data.label = 'document_list';
                    break;
                case 'price_details':
                    data.label = 'Customer Charges';
                    break;
                case 'is_stackable':
                    data.label = data.value ? 'Is Stackable' : '';
                    data.value = data.value ? data.value.toString() : 'false';
                    break;
                case 'is_dangerous_good':
                    data.label = data.value ? 'Is Dangerous Good' : '';
                    data.value = data.value ? data.value.toString() : 'false';
                    break;
                case 'dangerous_good_accessible':
                    data.label = data.value ? 'Dangerous Goods Accessible' : '';
                    data.value = data.value ? data.value.toString() : 'false';
                    break;
                case 'dangerous_good_un_number':
                    data.label = data.value ? 'Dangerous Good UN Number' : '';
                    break;
                case 'dangerous_good_name':
                    data.label = data.value ? 'Dangerous Good Name' : '';
                    break;
                case 'dangerous_good_class':
                    data.label = data.value ? 'Dangerous Good Class' : '';
                    break;
                case 'dangerous_good_division':
                    data.label = data.value ? 'Dangerous Good Division' : '';
                    break;
                case 'dangerous_good_category':
                    data.label = data.value ? 'Dangerous Good Category' : '';
                    break;
                case 'dangerous_good_packing_group':
                    data.label = data.value ? 'Dangerous Good Packing Group' : '';
                    break;
                case 'dangerous_good_category_type':
                    data.label = data.value ? 'Dangerous Good Category Type' : '';
                    break;
                case 'dangerous_good_category_packing_instruction':
                    data.label = data.value ? 'Dangerous Good Packing Instruction' : '';
                    break;
                case 'forward_reference_number':
                    data.label = data.value ? 'Forward Reference Number' : '';
                    break;
                case 'reverse_reference_number':
                    data.label = data.value ? 'Reverse Reference Number' : '';
                    break;
                case 'igst':
                    data.label = data.value ? 'IGST' : '';
                    break;
                case 'cgst':
                    data.label = data.value ? 'CGST' : '';
                    break;
                case 'sgst':
                    data.label = data.value ? 'SGST' : '';
                    break;
                case 'tax_details_receiver_gstin':
                    data.label = data.value ? 'Reciever GSTIN' : '';
                    break;
                case 'tax_details_sender_gstin':
                    data.label = data.value ? 'Sender GSTIN' : '';
                    break;
                case 'total_tax':
                    data.label = data.value ? 'Total Tax' : '';
                    break;
                case ' tax_percentage':
                    data.label = data.value ? 'Tax Percentage' : '';
                    break;
                case 'import_reference_number':
                    data.label = data.value ? 'Import Reference Number' : '';
                    break;
                case 'export_reference_number':
                    data.label = data.value ? 'Export Reference Number' : '';
                    break;
                case 'payment_collection_mode_list':
                    data.label = data?.value?.length ? data.label : '';
                    data.value = data?.value?.length ? data.value?.map((mode : string) => mode).join(', ') : '';
                    break;
                case 'delivery_time_slot_start':
                case 'delivery_time_slot_end':
                    data.value = consignment[key]
                        ? formatDateTime(consignment[key], 'datetime', 'DD MMM YYYY, HH:mm A') : 'N/A';
                    break;
                default:
                    break;
            }
        }

        if (data.value !== false && !data.value) {
            data.value = 'N/A';
        }
        if (!viewDataKeyLabelMap[key] && i18n.exists(key) && data.label?.trim() !== '') data.label = t(key);
        return data;
    };

    const renderLinkModal = (row: any) => {
        return (
            <DocumentDetailsModal
                isVisible={isVisible}
                onModalClose={() => setIsVisible(false)}
                documentDetails={row?.document_details}
            />
        );
    };
    const renderDocumentList = () => {
        return (
            <div className={isMobile ? classes.documentGroupMobile : classes.documentGroup}>
                <div className={isMobile ? classes.fieldTitleMobile : classes.fieldTitle}>Document List :</div>
                <div>
                    <Button
                        type="link"
                        onClick={() => {
                            setModalRow(consignment);
                            setIsVisible(true);
                        }}
                        className={classes.documentViewButton}
                    >
                        VIEW
                    </Button>
                </div>
            </div>
        );
    };
    const renderCustomValue = (key: string) => {
        switch (key) {
            case 'document_list':
                return (
                    <>
                        {renderDocumentList()}
                        {renderLinkModal(modalRow)}
                    </>
                );
            default:
                return null;
        }
    };

    const priceDetailsModalColumns = [
        {
            column_id: 'name',
            pretty_name: 'Charge Name',
            width: 50,
        },
        {
            column_id: 'value',
            pretty_name: 'Charge Value',
            width: 50,
        },
    ];


    const handlePriceDetailsModal = (priceDetailsList: any[]) => {
        setIsPriceDetailsModalVisible(true);
        setPriceDataList(priceDetailsList);
    };

    const renderFinalPrice = (finalPrice: string, priceDetailsList: any[], label: string) => {
        return (
            <span>
                {label === 'Customer Charges'
                    ? `${currencySymbol} ${(finalPrice || 0)?.toString()}`
                    : (finalPrice || 0)?.toString()}
                <Tooltip title="">
                    <a>
                        <InfoCircleOutlined
                            style={{ marginLeft: 5 }}
                            onClick={() => handlePriceDetailsModal(priceDetailsList)}
                        />
                    </a>
                </Tooltip>
            </span>
        );
    };


    const renderPriceDetailsData = (label: string, priceDetails: any, width: string, chargeKey: string | undefined) => {
        let finalPriceValue = '';
        let priceDetailsList = [];
        if (Array.isArray(priceDetails) && priceDetails.length) {
            const finalPriceObject: any = priceDetails.find((price) => (price.id === 'finalPrice'));
            if (finalPriceObject) {
                finalPriceValue = finalPriceObject.value;
                priceDetailsList = priceDetails.filter((price) => (price.id !== 'finalPrice'));
            }
        }
        return (
            <span className={isMobile ? classes.fieldGroupMobile : classes.fieldGroup} key={chargeKey} style={width ? { width } : undefined}>
                <div className={isMobile ? classes.fieldTitleMobile : classes.fieldTitle}>
                    {label}
                    {' : '}
                </div>
                <div className={isMobile ? classes.fieldValueMobile : classes.fieldValue}>
                    {finalPriceValue ? renderFinalPrice(finalPriceValue, priceDetailsList, label) : 'N/A'}
                </div>
            </span>
        );
    };

    const renderDeclaredValueField = (label: string, value: any, width: string, key: string | undefined) => {
        return (
            <span className={isMobile ? classes.fieldGroupMobile : classes.fieldGroup} key={key} style={width ? { width } : undefined}>
                <div className={isMobile ? classes.fieldTitleMobile : classes.fieldTitle}>
                    {label}
                    {' : '}
                </div>
                <div className={isMobile ? classes.fieldValueMobile : classes.fieldValue}>
                    {currencySymbol ? `${currencySymbol} ${value}` : value}
                </div>
            </span>
        );
    };

    const renderField = (key: string | undefined, width: string) => {
        if (key === undefined) {
            return (
                <div className={isMobile ? classes.fieldGroupMobile : classes.fieldGroup} style={width ? { width } : undefined} />
            );
        }
        const { label, value } = labelValuePair(key);
        if (label === '') return null;
        if (key === 'document_list') {
            return renderCustomValue(key);
        }

        if (key === 'price_details') {
            return renderPriceDetailsData(label, value, width, key);
        }

        if (key === 'declared_value') {
            return renderDeclaredValueField(label, value, width, key);
        }
        return (
            <span className={isMobile ? classes.fieldGroupMobile : classes.fieldGroup} key={key} style={width ? { width } : undefined}>
                <div className={isMobile ? classes.fieldTitleMobile : classes.fieldTitle}>
                    {label}
                    {' : '}
                </div>
                <div className={isMobile ? classes.fieldValueMobile : classes.fieldValue}>
                    {value}
                </div>
            </span>
        );
    };

    const renderPieceField = (idx: any, key: string|undefined) => {
        if (!key) return null;
        const data = {
            label: key,
            value: consignment.pieces_detail[idx][key] ?? 'N/A',
        };
        switch (key) {
            case 'length':
            case 'width':
            case 'height':
                if (data.value !== 'N/A') {
                    data.value = `${data.value} ${consignment.pieces_detail[idx]?.dimension_unit || 'cm'}`;
                }
                break;
            case 'declared_value':
                if (data.value !== 'N/A') {
                    data.value = `${data.value}  ${consignment.pieces_detail[idx]?.declared_currency || ''}`;
                }
                break;
            case 'weight':
                if (data.value !== 'N/A') {
                    data.value = `${data.value}  ${consignment.pieces_detail[idx]?.weight_unit || ''}`;
                }
                break;
            case 'reference_number': {
                const refNumber = consignment.pieces_detail[idx][key] || 'N/A';
                data.label = 'pieceReferenceNo';
                data.value = (
                    <Tooltip title={refNumber}>
                        {refNumber}
                    </Tooltip>
                );
                break;
            }
            case 'is_dangerous_good':
                data.value = (consignment.pieces_detail[idx][key]
                    && (consignment.pieces_detail[idx][key] === 'true' || consignment.pieces_detail[idx][key] === true))
                    ? 'true'
                    : 'false';
                data.label = consignment.pieces_detail[idx][key] !== null
                    && consignment.pieces_detail[idx][key] !== undefined
                    ? 'is_dangerous_good'
                    : '';
                break;
            case 'dangerous_good_accessible':
                data.value = (consignment.pieces_detail[idx][key]
                    && (consignment.pieces_detail[idx][key] === 'true' || consignment.pieces_detail[idx][key] === true))
                    ? 'true'
                    : 'false';
                data.label = consignment.pieces_detail[idx][key] !== null
                    && consignment.pieces_detail[idx][key] !== undefined
                    ? 'dangerous_good_accessible'
                    : '';
                break;
            case 'dangerous_good_un_number':
                data.label = consignment.pieces_detail[idx][key] ? 'dangerous_good_UN_number' : '';
                break;
            case 'dangerous_good_name':
                data.label = consignment.pieces_detail[idx][key] ? 'dangerous_good_name' : '';
                break;
            case 'dangerous_good_class':
                data.label = consignment.pieces_detail[idx][key] ? 'dangerous_good_class' : '';
                break;
            case 'dangerous_good_division':
                data.label = consignment.pieces_detail[idx][key] ? 'dangerous_good_division' : '';
                break;
            case 'dangerous_good_category':
                data.label = consignment.pieces_detail[idx][key] ? 'dangerous_good_category' : '';
                break;
            case 'dangerous_good_packing_group':
                data.label = consignment.pieces_detail[idx][key] ? 'dangerous_good_packing_group' : '';
                break;
            case 'dangerous_good_category_type':
                data.label = consignment.pieces_detail[idx][key] ? 'dangerous_good_category_type' : '';
                break;
            case 'dangerous_good_category_packing_instruction':
                data.label = consignment.pieces_detail[idx][key] ? 'dangerous_good_category_packing_instruction' : '';
                break;
            case 'purchase_order_number':
                data.label = consignment.pieces_detail[idx][key] ? 'purchase_order_number' : '';
                break;
            case 'piece_product_code':
                data.label = consignment.pieces_detail[idx][key] ? 'product_code' : '';
                break;
            case 'manufacturing_country':
                data.label = consignment.pieces_detail[idx][key] ? 'manufacturing_country' : '';
                break;
            case 'is_kit':
                data.value = consignment.pieces_detail[idx]?.additional_properties?.is_kit ? 'true' : 'false';
                data.label = consignment.pieces_detail[idx]?.additional_properties?.is_kit !== null
                    && consignment.pieces_detail[idx]?.additional_properties?.is_kit !== undefined
                    ? 'is_kit'
                    : '';
                break;
            case 'eccn':
                data.value = consignment.pieces_detail[idx]?.additional_properties?.eccn || 'N/A';
                data.label = consignment.pieces_detail[idx]?.additional_properties?.eccn ? 'eccn' : '';
                break;
            case 'hsn_code':
                data.value = consignment.pieces_detail[idx]?.additional_properties?.hsn_code || 'N/A';
                data.label = consignment.pieces_detail[idx]?.additional_properties?.hsn_code ? 'hs_code' : '';
                break;
            case 'status':
                data.value = `${prettyStatusNames[consignment.pieces_detail[idx]?.status] || snakeToPretty(consignment.pieces_detail[idx]?.status)}`;
                data.label = consignment.pieces_detail[idx]?.status ? 'status' : '';
                break;
            case 'payment_collection_mode_list':
                data.label = data?.value !== 'N/A' ? data.label : '';
                data.value = data?.value !== 'N/A' ? data.value?.map((mode : string) => mode).join(', ') : '';
                break;
            default:
                break;
        }
        return (
            data.label !== '' ? (
                <div className={classes.fieldGroup} key={`field-${data.label}`}>
                    <div className={classes.fieldTitle}>
                        {i18n.exists(data.label) ? t(data.label) : snakeToPretty(data.label)}
                        {' : '}
                    </div>
                    <div className={classes.fieldValue}>
                        {data.value || 'N/A'}
                    </div>
                </div>
            ) : null
        );
    };

    const renderItemField = (item: any, key: string | undefined) => {
        if (!key) return <div className={classes.fieldGroup} key={key} style={{ width: '50%' }} />;
        let name = snakeToPretty(t(key));
        switch (key) {
            case 'duty_value': name = t('total_value');
                break;
            case 'item_value': name = t('item_rate');
                break;
            default:
                break;
        }

        const value = item[key] ?? 'N/A';

        return (
            <div className={classes.fieldGroup} key={key} style={{ width: '50%' }}>
                <div className={classes.fieldTitle}>
                    {name}
                    :
                </div>
                <div className={classes.fieldValue}>
                    {value}
                </div>
            </div>
        );
    };

    const renderCustomFields = (key) => {
        const consignmentCustomFields = Object.entries(consignment?.custom_fields || {});
        const customField = consignmentCustomFields?.find(
            (field: any) => Array.isArray(field) && field?.length === 2 && field[0] === key.id,
        );
        const label = key.prettyName;
        const value = customField ? customField[1] : 'N/A';

        return (
            <span className={classes.fieldGroup} key={key} style={{ width: '50%' }}>
                <div className={classes.fieldTitle}>
                    {label}
                    :
                </div>
                <div className={classes.fieldValue}>
                    {(key.dataType === 'date' || key.dataType === 'datetime')
                        ? formatDateTime(value, key.dataType) : value}
                </div>
            </span>
        );
    };

    const renderPrimaryDetailsTab = () => {
        const detailsToShow = getDetailsToShowKeyList();
        const {
            shipmentDetailsKeys,
            taxAndCustomDetailsKeys,
            dangerousGoodKeys,
            codDetailsKeys,
            billingDetailsKeys,
        } = detailsToShow;

        let packageDetailsKeys = detailsToShow?.packageDetailsKeys;

        if (separateChildCN) {
            const excludedKeys = ['given_length', 'given_height', 'given_width', 'length', 'height', 'width'];
            packageDetailsKeys = packageDetailsKeys.filter((key): key is string => key !== undefined && !excludedKeys.includes(key));
        }

        const existingDangerousGoodKeys = dangerousGoodKeys.filter((key) => {
            const { label } = labelValuePair(key);
            return label !== undefined && label !== '' && label !== null;
        });

        return (
            // eslint-disable-next-line no-nested-ternary
            <div className={isMobile ? classes.detailsTabMobile : classes.detailsTab}>
                <span className={isMobile ? classes.sectionHeadingMobile : classes.sectionHeading}>{t('SHIPMENT_DETAILS')}</span>
                <div className={isMobile ? classes.sectionBoxMobile : classes.sectionBox}>
                    {shipmentDetailsKeys.map((key) => (
                        renderField(key, '50%')
                    ))}
                </div>
                <span className={isMobile ? classes.sectionHeadingMobile : classes.sectionHeading}>{t('TAX_AND_CUSTOM_DETAILS')}</span>
                <div className={isMobile ? classes.sectionBoxMobile : classes.sectionBox}>
                    {taxAndCustomDetailsKeys.map((key) => (
                        renderField(key, '50%')
                    ))}
                </div>
                <span className={isMobile ? classes.sectionHeadingMobile : classes.sectionHeading}>{t('PACKAGE_DETAILS')}</span>
                <div className={isMobile ? classes.sectionBoxMobile : classes.sectionBox}>
                    {packageDetailsKeys.map((key) => (
                        renderField(key, '50%')
                    ))}
                </div>

                <span className={isMobile ? classes.sectionHeadingMobile : classes.sectionHeading}>{t('COD_DETAILS')}</span>
                <div className={isMobile ? classes.sectionBoxMobile : classes.sectionBox}>
                    {codDetailsKeys.map((key) => (
                        renderField(key, '50%')
                    ))}
                </div>

                {billingDetailsKeys?.length > 0 && (
                    <>
                        <span className={isMobile ? classes.sectionHeadingMobile : classes.sectionHeading}>{t('BILLING_DETAILS')}</span>
                        <div className={isMobile ? classes.sectionBoxMobile : classes.sectionBox}>
                            {billingDetailsKeys.map((key) => (
                                renderField(key, '50%')
                            ))}
                        </div>
                    </>
                )}

                {existingDangerousGoodKeys.length > 0 && (
                    <>
                        <span className={isMobile ? classes.sectionHeadingMobile : classes.sectionHeading}>{t('DANGEROUS_GOODS_DETAILS')}</span>
                        <div className={isMobile ? classes.sectionBoxMobile : classes.sectionBox}>
                            {existingDangerousGoodKeys.map((key) => (
                                renderField(key, '50%')
                            ))}
                        </div>
                    </>
                )}

                {Object.keys(completeCustomFieldsList)?.length > 0 && (
                    <>
                        <span className={classes.sectionHeading}>{t('OTHER_DETAILS')}</span>
                        <div className={classes.sectionBox}>
                            {(completeCustomFieldsList).map((key) => (
                                renderCustomFields(key)
                            ))}
                        </div>
                    </>
                )}
            </div>
        );
    };

    const renderItemDetail = (item: any, idx: number) => {
        if (!item) return null;
        const keys = getDetailsToShowKeyList().itemDetails;
        return (
            <Collapse bordered={false} ghost>
                <Collapse.Panel
                    key={idx}
                    header={`${t('Item')} ${idx + 1}`}
                    className={classes.itemDetailsTab}
                >
                    <div className={classes.itemSectionBox}>
                        {keys.map((key: string|undefined) => (renderItemField(item, key)))}
                    </div>
                </Collapse.Panel>
            </Collapse>
        );
    };

    const renderPieceDetails = (piece: any, idx: number) => {
        const pieceKeys = getDetailsToShowKeyList().pieceDetails;
        return (
            <div>
                <span className={classes.sectionHeading}>{t('PIECE_DETAILS')}</span>
                <div className={classes.sectionBox}>
                    {
                        pieceKeys.map((key) => renderPieceField(idx, key))
                    }
                </div>
                {
                    piece?.pieces_item_details ? (
                        <>
                            {piece.pieces_item_details.map((item: any, iidx: number) => renderItemDetail(item, iidx))}
                        </>
                    ) : null
                }
            </div>
        );
    };

    const renderPieceCard = (piece: any, idx: number) => {
        const dimensions = ['length', 'width', 'height'];
        const isSelected = currentPieceNumber === idx;

        return (
            <div
                className={classes.pieceCard}
                style={isSelected ? { borderColor: '#006EC3' } : { borderColor: '#DDDFE9' }}
                onClick={() => setCurrentPieceNumber(idx)}
            >
                <div>
                    {!separateChildCN ? (
                        <div className={classes.pieceCardUpper}>
                            <div className={classes.pieceCardLeft}>
                                <span className={classes.pieceHeader}>
                                    {'   '}
                                    <ContentFilledIcon style={{
                                        color: '#6A3DF5',
                                        backgroundColor: 'transparent',
                                    }}
                                    />
                                    {'   '}
                                    {`Piece ${idx + 1}`}
                                </span>
                            </div>
                            <div className={classes.pieceCardRight}>
                                {piece?.pieces_item_details ? (
                                    <span>
                                        {piece?.pieces_item_details.length}
                                        {' ITEMS'}
                                    </span>
                                ) : null}
                                {piece.weight !== undefined && (
                                    <span>
                                        {piece.weight}
                                        {' '}
                                        {(consignment.pieces_detail[idx]?.weight_unit || '').toUpperCase()}
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className={classes.pieceCardSCNS}>
                            <span className={classes.pieceHeader}>
                                {'   '}
                                <ContentFilledIcon style={{
                                    color: '#6A3DF5',
                                    backgroundColor: 'transparent',
                                }}
                                />
                                {'   '}
                                {piece.reference_number}
                            </span>
                            <div className={classes.pieceCardStatusLine}>
                                <span className={classes.pieceCardStatus}>{`${prettyStatusNames[piece.status] || snakeToPretty(piece.status)}`}</span>
                                {piece?.pieces_item_details ? (
                                    <span>
                                        {piece?.pieces_item_details.length}
                                        {' ITEMS •'}
                                    </span>
                                ) : null}

                                {piece.weight !== undefined && (
                                    <span>
                                        {piece.weight}
                                        {' '}
                                        {(consignment.pieces_detail[idx]?.weight_unit || '').toUpperCase()}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                <>
                    {(piece.length && piece.width && piece.height) > 0 && (
                        <div className={classes.pieceDimensions}>
                            <>
                                {dimensions.map((dim, i) => (
                                    piece[dim] != null && (
                                        <span key={dim}>
                                            {piece[dim]}
                                            {i < dimensions.length - 1
                                            && dimensions.some((d) => d !== dim && piece[d] != null) ? ' x ' : ''}
                                        </span>
                                    )
                                ))}
                                {dimensions.some((dim) => piece[dim] != null)
                                && ` ${(consignment.pieces_detail[idx]?.dimension_unit || 'cm').toUpperCase()}`}
                            </>
                        </div>
                    )}
                    <span className={classes.pieceDescription}>{piece.description}</span>
                </>
            </div>
        );
    };

    const renderPiecesDetailsTab = () => {
        return (
            <div className={classes.pieceDetailsTab}>
                <div
                    className={classes.pieceDetailsTabLeft}
                    style={{ height: expandPage ? '65vh' : '70vh' }}
                >
                    <div className={classes.pieceCardColumnHeader}>
                        {t('PIECES_DETAILS')}
                        <span className={classes.piecesNumber}>{piecesArray.length}</span>
                    </div>
                    {piecesArray.map((piece: any, idx: number) => {
                        return renderPieceCard(piece, idx);
                    })}
                </div>
                <div
                    className={classes.pieceDetailsTabRight}
                    style={{ height: expandPage ? '65vh' : '70vh' }}
                >
                    <div className={classes.detailsTab}>
                        {renderPieceDetails(piecesArray[currentPieceNumber], currentPieceNumber)}
                    </div>
                </div>
            </div>
        );
    };

    const renderAddressDetailsTab = () => {
        const consignorDetailsAddressKeys = ['sender_address_line_1', 'sender_address_line_2'];
        const consigneeDetailsAddressKeys = ['destination_address_line_1', 'destination_address_line_2'];

        return (
            <div className={classes.detailsTab}>
                <div className={classes.addressDetails}>
                    <div className={classes.addressBox}>
                        <div className={classes.addressInfoUpper}>
                            <div className={classes.addressInfoUpperLeft}>
                                <span className={classes.addressType}>
                                    {'   '}
                                    <ContentFilledIcon style={{
                                        color: '#6A3DF5',
                                        backgroundColor: 'transparent',
                                    }}
                                    />
                                    {'   '}
                                    {t('CONSIGNOR_DETAILS')}
                                </span>
                                <div>
                                    <span className={classes.addressPincode}>
                                        {consignment.sender_pincode}
                                        {' '}
                                    </span>
                                    <span className={classes.addressCSC}>
                                        {consignment.sender_state ? `${consignment.sender_state}, ` : null}
                                        {consignment.sender_city ? `${consignment.sender_city}, ` : null}
                                        {consignment.sender_country ? `${consignment.sender_country}` : null}
                                    </span>
                                </div>
                            </div>
                            <div className={classes.addressInfoUpperRight}>
                                <span className={classes.addressName}>{consignment.sender_name}</span>
                                <span className={classes.addressContact}>{consignment.sender_phone}</span>
                            </div>
                        </div>
                        <div className={classes.horizontalSeperator} />
                        <div className={classes.addressInfoLower}>
                            {consignorDetailsAddressKeys.map((key) => (
                                renderField(key, '100%')
                            ))}
                            {consignment.sender_service_time_mins ? (
                                <div className={classes.addressServiceTimeBox}>
                                    <ClockCircleTwoTone />
                                    {' '}
                                    <span>
                                        {consignment.sender_service_time_mins}
                                        {' MINS'}
                                    </span>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <div className={classes.addressVerticalJoin} />
                    <div className={classes.verticalSpace} />
                    <div className={classes.addressVerticalJoin} />
                    <div className={classes.verticalSpace} />
                    <div className={classes.addressVerticalJoin} />

                    <div className={classes.addressBox}>
                        <div className={classes.addressInfoUpper}>
                            <div className={classes.addressInfoUpperLeft}>
                                <span className={classes.addressType}>
                                    {'   '}
                                    <EnvironmentFilled style={{
                                        color: '#5CAF7E',
                                    }}
                                    />
                                    {'   '}
                                    {t('CONSIGNEE_DETAILS')}
                                </span>
                                <div>
                                    <span className={classes.addressPincode}>
                                        {consignment.destination_pincode}
                                        {' '}
                                    </span>
                                    <span className={classes.addressCSC}>
                                        {consignment.destination_state ? `${consignment.destination_state}, ` : null}
                                        {consignment.destination_city ? `${consignment.destination_city}, ` : null}
                                        {consignment.destination_country ? `${consignment.destination_country}` : null}
                                    </span>
                                </div>
                            </div>
                            <div className={classes.addressInfoUpperRight}>
                                <span className={classes.addressName}>{consignment.destination_name}</span>
                                <span className={classes.addressContact}>{consignment.destination_phone}</span>
                            </div>
                        </div>
                        <div className={classes.horizontalSeperator} />
                        <div className={classes.addressInfoLower}>
                            {consigneeDetailsAddressKeys.map((key) => (
                                renderField(key, '100%')
                            ))}
                            {consignment.destination_service_time_mins ? (
                                <div className={classes.addressServiceTimeBox}>
                                    <ClockCircleTwoTone />
                                    {' '}
                                    <span>
                                        {consignment.destination_service_time_mins}
                                        {' MINS'}
                                    </span>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderDetails = () => {
        switch (selectedTab) {
            case 'primary_details':
                return (
                    <>
                        {renderPrimaryDetailsTab()}
                    </>
                );
            case 'pieces_details':
                return (
                    <>
                        {renderPiecesDetailsTab()}
                    </>
                );
            case 'address_details':
                return (
                    <>
                        {renderAddressDetailsTab()}
                    </>
                );
            default:
                return (<h3>Tab details not handled yet</h3>);
        }
    };

    const renderImagePreview = () => {
        if (!imageUrl) return null;
        return (
            <Image
                src={imageUrl}
                width={0}
                preview={{
                    visible: true,
                    src: imageUrl,
                    onVisibleChange: (value) => {
                        if (!value) setImageUrl(null);
                    },
                }}
            />
        );
    };

    return (
        <>
            <div className={classes.mainDiv}>
                {renderDetails()}
                {renderImagePreview()}
            </div>
            {isPriceDetailsModalVisible && (
                <TableModal
                    width={500}
                    header="Details"
                    isVisible={isPriceDetailsModalVisible}
                    handleModalClose={() => {
                        setIsPriceDetailsModalVisible(false);
                        setPriceDataList([]);
                    }}
                    dataSource={priceDataList}
                    columnsToShow={priceDetailsModalColumns}
                />
            )}
        </>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    const { master } = state;
    const { parts_to_show } = master;
    const separateChildCN = parts_to_show?.separate_child_cns;
    const currencySymbol = master?.config?.currency_symbol || '';
    const completeCustomFieldsList = master?.config?.complete_custom_fields_list || [];
    return {
        consignmentSectionList: state.genericPage?.[PAGE_NAMES.CONSIGNMENTS]?.metadata?.sectionList,
        separateChildCN,
        currencySymbol,
        completeCustomFieldsList,
    };
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: consignmentDetailsIntStyles,
    },
    connectRouter: true,
};

export default GenericHoc(hocConfig)(ConsignmentDetailedInternational);
